import type { Cart, CartSummary } from '~/types/cart'

export function initCart (): Cart {
  return {
    createdDate: new Date(),
    id: '',
    items: [],
    culture: '',
    email: '',
    itemsSubTotal: 0,
    promotionalItems: [],
    purchasableItems: [],
    subTotal: '',
    totalProducts: 0,
    demonstrator: null,
    couponCodes: [],
    couponTotal: 0,
    isFromDbws: false,
    isDemoCart: false,
    groupShippingCode: ''
  }
}

export function initCartSummary (): CartSummary {
  return {
    total: 0,
    formattedTotal: '',
    subTotal: 0,
    formattedSubTotal: '',
    formattedShipping: '$0.00',
    couponDiscount: 0,
    formattedCouponDiscount: '',
    amountEligibleForInstantIncome: 0,
    formattedAmountEligibleForInstantIncome: '',
    instantIncomePercent: 0,
    formattedInstantIncomePercent: '',
    instantIncomeAmount: 0,
    formattedInstantIncomeAmount: '',
    commissionableMerchandiseAmount: 0,
    formattedCommissionableMerchandiseAmount: '',
    nonCommissionableMerchandiseAmount: 0,
    formattedNonCommissionableMerchandiseAmount: '',
    commissionableProductAmount: 0,
    formattedCommissionableProductAmount: '',
    nonCommissionableProductAmount: 0,
    formattedNonCommissionableProductAmount: '',
    commissionableRetailValue: 0,
    formattedCommissionableRetailValue: '',
    nonCommissionableRetailValue: 0,
    formattedNonCommissionableRetailValue: '',
    csvAmount: 0,
    formattedCsvAmount: '',
    groupShippingCodeDemoName: '',
    apiError: {
      errors: []
    }
  }
}

// Ported from nuxt2 `utils/cart.ts`. The nuxt2 signatures took the `$auth`
// plugin instance; here the same three facts come off `useSAuthState()`:
// `isAuthenticated` (already excludes Anonymous/Agent), `isAgent` (the nuxt2
// `authStore.isAgent` role check) and `user.defaultCulture`.
//
// The stores are left to Nuxt's auto-imports purely as house style. It buys no
// cycle avoidance: unimport rewrites `useCartStore()` into a real static import
// at build time, and `~/stores/cart` imports `initCart`/`initCartSummary` back
// from this file, so the cycle exists either way. It is harmless — the store
// factories are only called inside these function bodies, long after both
// modules have finished evaluating — and nuxt2's `utils/cart.ts` imported the
// stores explicitly and shipped the same cycle for years.
export const cartMerge = async () => {
  // fixes 405 PATCH - if there is no cart id just fetch cart
  const rootStore = useRootStore()
  const cartStore = useCartStore()
  const localeStore = useLocaleStore()
  const { isAuthenticated, isAgent, user } = useSAuthState()

  if (!cartStore.cart.id || cartStore.cart.id === '') {
    await cartStore.fetchCart()
    return
  }

  // merge cart even if there are no items because they may have a demo they need to keep
  if (isAuthenticated.value && !isAgent.value && !rootStore.timeoutMenu) {
    // if these are not the same do not get the cart since the app is
    // redirecting to the default locale culture of the user
    if (localeStore.culture !== user.value?.defaultCulture) { return }

    const cart = Object.assign({}, cartStore.cart)
    // a demo cart is already the demonstrator's own cart - merging an anonymous
    // cart into it would move someone else's items onto the demo order
    if (cart.isDemoCart) { return }
    await cartStore.mergeCarts(cart)
  } else {
    await cartStore.fetchCart()
  }
}

export const cartReplace = async (force?: boolean) => {
  // fixes 405 PATCH - if there is no cart id just fetch cart
  const rootStore = useRootStore()
  const cartStore = useCartStore()
  const localeStore = useLocaleStore()
  const { isAuthenticated, isAgent, user } = useSAuthState()

  if (!cartStore.cart.id || cartStore.cart.id === '') {
    await cartStore.fetchCart()
    return
  }

  // dont replace cart for sharedCart

  // replace cart even if there are no items because they may have a demo they need to keep
  if ((isAuthenticated.value && !isAgent.value && !rootStore.timeoutMenu) || force) {
    // if these are not the same do not get the cart since the app is
    // redirecting to the default locale culture of the user
    if (localeStore.culture !== user.value?.defaultCulture) { return }
    await cartStore.replaceCart()
  } else {
    await cartStore.fetchCart()
  }
}
